/**
 * Azkar: the tasbih counter's phrases, and the rules for a count sent to the mosque (owner, 2026-11-02: "this tasbih
 * counter can send to the mosque admin panel if the user wants — collected and seen there, registered against the user").
 *
 * The idea is the MKK portal's Halaqa-e-Darood count (people send what they recited towards one gathering, the board
 * sees the total), made a platform feature: a mosque opens a «tasbih-innsamling» (a collection — a title, optionally one
 * phrase and a goal, optionally an end date) and members send their count to it from the app. Counting itself never
 * leaves the phone; only a count the member chooses to send does.
 *
 * Religious practice is a special category of personal data (GDPR art. 9): a count is linked to the person only when
 * they choose «Send med navn» for that one count, and the app says, on the button, that the mosque will see their name.
 * «Send anonymt» stores no person at all.
 *
 * `name` is the short label (a chip, the staff panel's list); `tr` is the full transliteration under the Arabic.
 *
 * TO BE REVIEWED by the mosque's imam before a release carries it (as duas.js): Arabic, transliteration and meanings.
 */
const PHRASES = [
  {
    id: 'subhanallah', name: 'Subḥān Allāh', target: 33,
    ar: 'سُبْحَانَ اللّٰهِ', tr: 'Subḥān Allāh',
    no: 'Allah er fri for enhver feil', en: 'Glory be to Allah', ur: 'اللہ پاک ہے',
  },
  {
    id: 'alhamdulillah', name: 'Al-ḥamdu lillāh', target: 33,
    ar: 'اَلْحَمْدُ لِلّٰهِ', tr: 'Al-ḥamdu lillāh',
    no: 'All lovprisning tilhører Allah', en: 'All praise is for Allah', ur: 'تمام تعریفیں اللہ کے لیے ہیں',
  },
  {
    id: 'allahuakbar', name: 'Allāhu akbar', target: 34,
    ar: 'اَللّٰهُ أَكْبَرُ', tr: 'Allāhu akbar',
    no: 'Allah er størst', en: 'Allah is the Greatest', ur: 'اللہ سب سے بڑا ہے',
  },
  {
    id: 'tahlil', name: 'Lā ilāha illallāh', target: 100,
    ar: 'لَا إِلٰهَ إِلَّا اللّٰهُ', tr: 'Lā ilāha illallāh',
    no: 'Det finnes ingen gud unntatt Allah', en: 'There is no god but Allah', ur: 'اللہ کے سوا کوئی معبود نہیں',
  },
  {
    id: 'astaghfirullah', name: 'Astaghfirullāh', target: 100,
    ar: 'أَسْتَغْفِرُ اللّٰهَ', tr: 'Astaghfirullāh',
    no: 'Jeg ber Allah om tilgivelse', en: 'I seek Allah’s forgiveness', ur: 'میں اللہ سے بخشش مانگتا ہوں',
  },
  {
    id: 'salawat', name: 'Ṣalawāt (durood)', target: 100,
    ar: 'اَللّٰهُمَّ صَلِّ عَلٰى مُحَمَّدٍ وَّعَلٰى اٰلِ مُحَمَّدٍ', tr: 'Allāhumma ṣalli ʿalā Muḥammadin wa ʿalā āli Muḥammad',
    no: 'Allah, send Din velsignelse over Muhammad og Muhammads familie',
    en: 'O Allah, send blessings upon Muhammad and the family of Muhammad',
    ur: 'اے اللہ! محمد ﷺ اور آلِ محمد پر رحمت نازل فرما',
  },
];
const BY_ID = new Map(PHRASES.map((p) => [p.id, p]));
const phrase = (id) => BY_ID.get(id) || null;

/** One sent count can be at most this — a day of steady counting is a few thousand; more is a mistake or abuse. */
const MAX_COUNT = 100000;
/** Collection limits, shared by the API and the staff panel. */
const LIMITS = { title: 80, note: 300, target: 100000000 };

/**
 * A count the app sends: { count, phraseId?, anonymous, clientId }. `clientId` makes a retry safe (the same id twice is
 * one entry). Returns { ok: true, value } or { ok: false, errors: [{ field, reason }] }.
 */
function checkEntry(body, collection) {
  const errors = [];
  const b = body && typeof body === 'object' ? body : {};
  const count = b.count;
  if (!Number.isInteger(count) || count < 1) errors.push({ field: 'count', reason: 'required' });
  else if (count > MAX_COUNT) errors.push({ field: 'count', reason: 'too_large' });
  let phraseId = b.phraseId == null || b.phraseId === '' ? null : String(b.phraseId);
  if (phraseId && !BY_ID.has(phraseId)) errors.push({ field: 'phraseId', reason: 'unknown' });
  // A collection for one phrase takes counts of that phrase only (a durood collection is not a total of everything).
  if (collection && collection.phraseId) {
    if (phraseId && phraseId !== collection.phraseId) errors.push({ field: 'phraseId', reason: 'other_phrase' });
    phraseId = collection.phraseId;
  }
  if (typeof b.anonymous !== 'boolean') errors.push({ field: 'anonymous', reason: 'required' });
  const clientId = String(b.clientId || '');
  if (!/^[A-Za-z0-9-]{8,64}$/.test(clientId)) errors.push({ field: 'clientId', reason: 'invalid' });
  return errors.length ? { ok: false, errors } : { ok: true, value: { count, phraseId, anonymous: b.anonymous, clientId } };
}

/** A collection as staff save it: { title, phraseId?, target?, endsOn? (YYYY-MM-DD), note? }. */
function checkCollection(body) {
  const errors = [];
  const b = body && typeof body === 'object' ? body : {};
  const title = String(b.title || '').trim();
  if (!title) errors.push({ field: 'title', reason: 'required' });
  else if (title.length > LIMITS.title) errors.push({ field: 'title', reason: 'too_long' });
  const phraseId = b.phraseId == null || b.phraseId === '' ? null : String(b.phraseId);
  if (phraseId && !BY_ID.has(phraseId)) errors.push({ field: 'phraseId', reason: 'unknown' });
  const target = b.target == null || b.target === '' ? null : Number(b.target);
  if (target !== null && (!Number.isInteger(target) || target < 1 || target > LIMITS.target)) errors.push({ field: 'target', reason: 'invalid' });
  const endsOn = b.endsOn == null || b.endsOn === '' ? null : String(b.endsOn);
  if (endsOn && !/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(endsOn)) errors.push({ field: 'endsOn', reason: 'invalid' });
  const note = b.note == null ? null : String(b.note).trim() || null;
  if (note && note.length > LIMITS.note) errors.push({ field: 'note', reason: 'too_long' });
  return errors.length ? { ok: false, errors } : { ok: true, value: { title, phraseId, target, endsOn, note } };
}

/** Open for counts: not closed by staff, and not past its last day (`today` is the mosque's wall-clock date). */
const isOpen = (c, today) => Boolean(c) && !c.closedAt && (!c.endsOn || c.endsOn >= today);

/** The meaning in the member's language: Urdu → English → Norwegian (as duas.meaning). */
function meaning(p, lang = 'no') {
  return (lang === 'ur' && p.ur) || (lang !== 'no' && p.en) || p.no;
}

module.exports = { PHRASES, phrase, MAX_COUNT, LIMITS, checkEntry, checkCollection, isOpen, meaning };
